import { useApiClient } from "@/hooks/useApiClient";
import useSWR from "swr";
import useSWRMutation, { type SWRMutationResponse } from "swr/mutation";
import {
  type AcquireBookZenbujikou,
  type AcquireBookPictures,
  type PictureReuseRequest,
} from "@/types/acquirebook";
import { isAxiosError } from "axios";

const url = "/picture-request/acquire-reuse";

export type ReuseInfoGetAPIReuseOption = "REUSE_OWN" | "REUSE_TRUSTART";

export interface ReuseInfoGetAPIResponse {
  pictureRequestId: number; // 謄本・図面依頼ID
  pictureRequestDetailId: number; // 謄本・図面依頼詳細ID
  type: AcquireBookPictures; // 登記/図面の種類
  zenbujikoOptions: AcquireBookZenbujikou[]; // typeが全部事項の場合のオプション設定値
  reuseOption: ReuseInfoGetAPIReuseOption; // 新規取得かリユース登記か取得済登記かを識別するための識別子
  acquiredDateReuseTrustart: Date | null; // リユース登記の取得日時、reuseOptionがREUSE_TRUSTARTの場合は必ず値が入る
  acquiredDateReuseOwn: Date | null; // 取得済登記の取得日時、reuseOptionがREUSE_OWNの場合は必ず値が入る
}

/**
 * 再利用情報取得API
 * @returns 成功ならBookReuse[]、失敗ならundefined
 */
export const ReuseInfoGetAPI = (
  params: PictureReuseRequest,
  shouldFetch: boolean
): ReuseInfoGetAPIResponse[] | undefined => {
  const { apiClient } = useApiClient();

  const fetcher = async (
    args: [string, PictureReuseRequest]
  ): Promise<ReuseInfoGetAPIResponse[]> => {
    const [url, params] = args;

    const { data } = await apiClient
      .get<ReuseInfoGetAPIResponse[]>(url, { params })
      .catch((reason) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (reason.response && reason.response.status === 404) {
          return { data: [] };
        }
        throw reason;
      });
    return data;
  };

  const { data } = useSWR<ReuseInfoGetAPIResponse[] | undefined>(
    shouldFetch ? [url, params] : null,
    fetcher,
    {
      revalidateOnFocus: true,
    }
  );
  return data;
};

/**
 * 再利用情報取得API単発版
 * @returns trigger()が返ってくるのでtrigger(data: PictureReuseRequest)で設定を送信することができます
 */
export const ReuseInfoGetMutateAPI = (): SWRMutationResponse<
  ReuseInfoGetAPIResponse[],
  never,
  string,
  PictureReuseRequest
> => {
  const { apiClient } = useApiClient();

  const fetcher = async (
    url: string,
    { arg }: { arg: PictureReuseRequest }
  ): Promise<ReuseInfoGetAPIResponse[]> => {
    try {
      const response = await apiClient.get<ReuseInfoGetAPIResponse[]>(url, {
        params: arg,
      });
      return response.data;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 401) {
        const retry = await apiClient.get<ReuseInfoGetAPIResponse[]>(url, {
          params: arg,
        });
        return retry.data;
      }
      return [];
    }
  };

  return useSWRMutation(url, fetcher, { revalidate: false });
};
